import { ArrowDownwardRounded, DeleteRounded, HorizontalRule } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { APP_LOCAL_DATE_FORMAT, APP_TIMESTAMP_FORMAT_YEAR, AUTHORITIES } from 'app/config/constants';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { ITableComponentProps } from 'app/shared/interfaces/tableComponentInterface';
import { showElement } from 'app/shared/util/data-utils';
import { convertDateTimeToPtBR } from 'app/shared/util/date-utils';
import { isAdminByCompanyType, translateSystemRole } from 'app/shared/util/role-utils';
import React from 'react';
import { TextFormat, Translate, openFile } from 'react-jhipster';
import { Link } from 'react-router-dom';

export const TableComponent: React.FC<ITableComponentProps<any>> = ({
  listData,
  headers,
  list,
  match,
  sortFunction,
  pagination,
  setSelect,
  groupButtonIcons,
  buildDashboardUrl,
  hideTrailingHeaderCell = false,
}) => {
  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const renderValue = (data, item) => {
    if (Array.isArray(data.value)) {
      return data.value.map((value: string) => item[value]).join(' ');
    } else {
      // Para utilização de objetos aninhados
      const splitString = data.value?.split('?.');
      for (const prop of splitString) {
        if (item == null) return null;
        item = item[prop];
      }

      return item;
    }
  };

  return (
    <>
      <TableContainer sx={{ marginTop: '25px' }}>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header, i) => (
                <TableCell
                  key={`entity-${i}`}
                  sx={{
                    borderBottom: 'none',
                    paddingBottom: 1,
                  }}
                >
                  {header.sortable ? (
                    <TableSortLabel
                      active={pagination.sort === header.key}
                      direction={pagination.order as 'desc' | 'asc'}
                      onClick={sortFunction(header.key)}
                      IconComponent={ArrowDownwardRounded}
                    >
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {header.component}
                      </Typography>
                    </TableSortLabel>
                  ) : (
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header.component}
                    </Typography>
                  )}
                </TableCell>
              ))}
              {!hideTrailingHeaderCell && <TableCell sx={{ borderBottom: '0' }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((item, itemIndex: number) => (
              <TableRow key={itemIndex} sx={{ filter: item.activated === false ? 'grayscale(1)' : 'none' }}>
                {listData.map((data, index: number) => (
                  <TableCell
                    key={index}
                    sx={{
                      backgroundColor: 'background.paper',
                      borderBottom: '8px solid',
                      borderColor: 'primary.main',
                      display: data.onlyAdmin && !isAdmin && !isRepresentativeAdmin && !isManufacturerAdmin ? 'none' : 'table-cell',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {(() => {
                      switch (data.type) {
                        case 'text':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {renderValue(data, item)}
                            </Typography>
                          );
                        case 'translateText': {
                          const translatedValue = renderValue(data, item);
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {translatedValue != null ? (
                                <Translate contentKey={`${data.translate}.${translatedValue}`} />
                              ) : (
                                <HorizontalRule fontSize="small" />
                              )}
                            </Typography>
                          );
                        }
                        case 'date':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {item[data.value] ? <TextFormat type="date" value={item[data.value]} format={APP_LOCAL_DATE_FORMAT} /> : null}
                            </Typography>
                          );
                        case 'datetime':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem" fontFamily="Public Sans">
                              {item[data.value] ? <TextFormat type="date" value={item[data.value]} format={APP_TIMESTAMP_FORMAT_YEAR} /> : null}
                            </Typography>
                          );
                        case 'badgeAuthorities':
                          return (
                            <Chip
                              label={translateSystemRole(item[data.value])}
                              sx={{
                                color: 'primary.contrastText',
                                fontSize: '0.875rem',
                                minWidth: '120px',
                              }}
                            />
                          );
                        case 'stateButton':
                          return (
                            <Chip
                              label={
                                <Translate
                                  contentKey={item?.[data.value] ? 'fleetSenseApp.TableComponent.activated' : 'fleetSenseApp.TableComponent.deactivated'}
                                />
                              }
                              sx={{
                                fontSize: '0.875rem',
                                borderColor: item?.[data.value] === true ? 'success.main' : 'error.main',
                                backgroundColor: item?.[data.value] === true ? 'success.main' : 'error.main',
                                color: item?.[data.value] === true ? 'success.contrastText' : 'error.contrastText',
                              }}
                            />
                          );
                        case 'editButton':
                          return (
                            (!data.showIfCompanyIsOwner || !isAdminByCompanyType(item.company?.type) || isAdmin) && (
                              <IconButton
                                component={Link}
                                to={`${match.url}/${item?.[data.value]}/edit`}
                                size="small"
                                style={showElement(isAdmin || isRepresentativeAdmin || isManufacturerAdmin || isTransporterAdmin)}
                              >
                                <EditIcon color="secondary" />
                              </IconButton>
                            )
                          );
                        case 'deleteButton':
                          return (
                            (!data.showIfCompanyIsOwner || !isAdminByCompanyType(item.company?.type) || isAdmin) && (
                              <IconButton
                                component={Link}
                                to={`${match.url}/${item?.[data.value]}/delete`}
                                size="small"
                                style={showElement(isAdmin || isRepresentativeAdmin || isManufacturerAdmin || isTransporterAdmin)}
                              >
                                <DeleteRounded color="error" />
                              </IconButton>
                            )
                          );
                        case 'viewButton':
                          return (
                            <IconButton onClick={() => setSelect(item)} size="small">
                              <VisibilityIcon color="secondary" />
                            </IconButton>
                          );
                        case 'groupButtonIcons':
                          return (
                            groupButtonIcons?.length && (
                              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                {groupButtonIcons.map((button, i) => (
                                  <IconButton key={i} component={Link} to={`${match.url}/${item?.[data.value]}/${button.path}`} size="small">
                                    {button.icon}
                                  </IconButton>
                                ))}
                              </Box>
                            )
                          );
                        case 'image':
                          return (
                            item?.[data.value] !== null &&
                            item?.[data.value] !== '' && (
                              <a onClick={openFile('image/jpeg', item?.[data.value])}>
                                <img src={`data:image/*;base64,${item?.[data.value]}`} style={{ maxHeight: '30px' }} />
                              </a>
                            )
                          );
                        case 'object':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem" fontFamily="Public Sans">
                              {item[data.value] ? item[data.value][data.label] : ''}
                            </Typography>
                          );
                        case 'boolean':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem" fontFamily="Public Sans">
                              {item[data.value] ? 'Sim' : 'Não'}
                            </Typography>
                          );
                        case 'customButton':
                          return (
                            <IconButton onClick={() => data.action && data.action(item)} size="small">
                              {[data.icon]}
                            </IconButton>
                          );
                        case 'convertDateTimeToPtBR':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {convertDateTimeToPtBR(renderValue(data, item))}
                            </Typography>
                          );
                        case 'fixedNumber':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {Number(renderValue(data, item)).toFixed()}
                            </Typography>
                          );
                        case 'child':
                          return data.renderTableChild && data.renderTableChild(item);
                        case 'assetClassificationKpi':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {(() => {
                                const indicator = data.getIndicator ? data.getIndicator(item.indicators, data.value) : null;
                                if (indicator === null || indicator === undefined || isNaN(indicator)) {
                                  return '—';
                                }
                                return Number.isInteger(indicator) ? indicator : indicator.toFixed(2);
                              })()}
                            </Typography>
                          );

                        case 'assetClassificationStatistics':
                          return (
                            <Typography color="primary.contrastText" fontSize="0.875rem">
                              {(() => {
                                if (!item) return '—';
                                try {
                                  const value = renderValue(data, item);
                                  if (value === null || value === undefined || isNaN(value)) return '—';
                                  return Number.isInteger(value) ? value : Number(value).toFixed(2);
                                } catch {
                                  return '—';
                                }
                              })()}
                            </Typography>
                          );
                        case 'assetClassificationLicensePlate': {
                          // Add a link to redirect to ControlTower main page (with the licensePlate)
                          const licensePlate: string = renderValue(data, item) as string;

                          const url = buildDashboardUrl
                            ? buildDashboardUrl(licensePlate)
                            : `/asset-classification/dashboard${licensePlate ? `?licensePlate=${encodeURIComponent(licensePlate)}` : ''}`;

                          return (
                            <Link to={url}>
                              <Typography color="primary.contrastText" fontSize="0.875rem">
                                {licensePlate}
                              </Typography>
                            </Link>
                          );
                        }
                        default:
                          return null;
                      }
                    })()}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
